import { z } from "zod";

/**
 * The Plugins panel's wire (ARCHITECTURE §6.18): the catalogue of apps the
 * user can connect through Composio, and the connections they have. The
 * Composio key stays in the box (or, hosted, on the router); the page only
 * ever sees what is here.
 */

/** GET /plugins: whether this box has a Composio key at all. Without one the panel says so and lists nothing. */
export const PluginsStatusSchema = z.object({ configured: z.boolean() }).strict();
export type PluginsStatus = z.infer<typeof PluginsStatusSchema>;

export const PluginToolkitSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  /** An image URL on Composio's side; empty when they have none. */
  logo: z.string(),
  categories: z.array(z.string()),
  toolsCount: z.number().int().nonnegative(),
  /** Needs no sign-in at all, so there is nothing to connect. */
  noAuth: z.boolean(),
}).strict();
export type PluginToolkit = z.infer<typeof PluginToolkitSchema>;

/** GET /plugins/toolkits?search=&cursor=: one page of the catalogue, most used first unless searching. */
export const PluginToolkitPageSchema = z.object({ items: z.array(PluginToolkitSchema), nextCursor: z.string().nullable(), total: z.number().int().nonnegative() }).strict();
export type PluginToolkitPage = z.infer<typeof PluginToolkitPageSchema>;

/** Active works; pending is a sign-in the user started and has not finished; broken needs connecting again. */
export const PluginConnectionStateSchema = z.enum(["active", "pending", "broken"]);
export type PluginConnectionState = z.infer<typeof PluginConnectionStateSchema>;

export const PluginConnectionSchema = z.object({
  id: z.string().min(1),
  toolkit: z.string().min(1),
  state: PluginConnectionStateSchema,
  /** Composio's own word, for a tooltip: ACTIVE, INITIATED, EXPIRED, ... */
  status: z.string(),
  createdAt: z.string(),
}).strict();
export type PluginConnection = z.infer<typeof PluginConnectionSchema>;

/** GET /plugins/connections. */
export const PluginConnectionListSchema = z.object({ connections: z.array(PluginConnectionSchema) }).strict();
export type PluginConnectionList = z.infer<typeof PluginConnectionListSchema>;

/** POST /plugins/connections: start connecting an app. */
export const ConnectPluginRequestSchema = z.object({ toolkit: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,99}$/i, "toolkit slugs are letters, digits, _ and -") }).strict();
export type ConnectPluginRequest = z.infer<typeof ConnectPluginRequestSchema>;

/** Where the user signs in; the connection is pending until they finish there. */
export const ConnectPluginResponseSchema = z.object({ url: z.string().url(), connectionId: z.string() }).strict();
export type ConnectPluginResponse = z.infer<typeof ConnectPluginResponseSchema>;
